/*
 * HONEYWELL INC.,
 * COPYRIGHT (c) 2022
 * ALL RIGHTS RESERVED
 *
 * This software is a copyrighted work and/or information protected as a trade secret.
 * Legal rights of Honeywell Inc. in this software is distinct from ownership of any medium
 * in which the software is embodied. Copyright or trade secret notices included must be
 * reproduced in any copies authorized by Honeywell Inc.
 *
 */

import React, { useState, useEffect } from 'react';
import { DataTable } from '@scuf/datatable';
import { ICellData } from '@scuf/datatable/dist/components/DataTable/IDataTableInterfaces';
import {
  CarbonDetailMeterField,
  EnergyDetailMeterField
} from '../Utils/Constants';
import '../DetailBreakdownByMeter/CarbonDetailTabBreakdownByMeterTheme.scss';
import { ICarbonDetailTabBreakdownByScopeProps } from './ICarbonDetailTabBreakdownByScopeProps';
import { getMaxValueIndexForField } from '../Utils/EnergyUsagesViewDetailsUtils';
import { renderTableColumns } from '../MeterSavingsDetailTable/MeterSavingsDetailTableUtility';
import { IAny } from '../../DashboardKPIWidgets/IEnergyManagementProps';
import { IDataTableColumn } from '../MeterSavingsDetailTable/IMeterSavingsDetailTableProps';
import { Icon } from '@scuf/common';
// import {
//   getcellDataRendererForChildren,
//   getcellDataRenderer
// } from './DetailBreakdownByScopeUtils';
// import { ICellData } from '@scuf/datatable/dist/components/DataTable/IDataTableInterfaces';
// import { CarbonDetailMeterField } from '../Utils/Constants';

import CommonProgressBar from '../../shared/CommonProgressBar/CommonProgressBar';
import {
  getConvertedUnit,
  getConvertedValues,
  getNullConverted,
  getNullToZero
} from '../Utils/EnergyUsagesViewDetailsUtils';

import { getPercentageColor } from '../../DetailKPIWidgets/PortfolioEnergyUsageViewDetails/PremiumSubscription/AllUtilitiesDetails/BottomSection/TableColumnRenderUtils';
import { Badge } from '@scuf/common';
import { getConvertedValue } from '../Utils/unitHandling/unitHandling';
import { ConversionType } from '../Utils/unitHandling/UnitModel';

const getcellDataRendererForChildren = (
  cellData: ICellData,
  subUtilityMeasurementUnit: string | undefined,
  progressBarRelativeWidth?: number,
  emissionMeasurementUnit?: string
) => {
  const { value, field, rowData } = cellData;

  if (field === CarbonDetailMeterField.change) {
    const convertedValue: IAny = getConvertedValue(
      ConversionType.CARBON,
      emissionMeasurementUnit ? emissionMeasurementUnit : '',
      value,
      rowData.changeUnit
    )?.Value;
    if (value === null) {
      return <>--</>;
    }
    return (
      <span className="score-category-meterdata-meter">
        <span
          className="savings-badge"
          style={{
            color: getPercentageColor(convertedValue)
          }}
        >
          {getConvertedValues(convertedValue)}
          {' ' +
            rowData.changeUnit +
            '(' +
            getConvertedValues(rowData.percentageConsumptionSaving) +
            '%' +
            ')'}
        </span>
      </span>
    );
  }
  if (field === CarbonDetailMeterField.baselineConsumption) {
    const convertedValue = getConvertedValue(
      ConversionType.CARBON,
      emissionMeasurementUnit ? emissionMeasurementUnit : '',
      value,
      rowData.baselineUnit
    )?.Value;
    return (
      <span className="score-category-meterdata-meter">
        <div className="consumptionvalue">{convertedValue}</div>
        {rowData.baselineUnit}
      </span>
    );
  }
  if (field === CarbonDetailMeterField.actualConsumption) {
    const convertedValue = getConvertedValue(
      ConversionType.CARBON,
      emissionMeasurementUnit ? emissionMeasurementUnit : '',
      rowData.actualConsumption,
      rowData.actualUnit
    )?.Value;
    return (
      <span className="emaas-carbondetail-category-meterdata">
        <span className="score-category-percent">
          <div className="measurementunit">
            {getNullConverted(convertedValue)}
          </div>
          {rowData.actualUnit}
        </span>
        <span className="score-category-progress">
          <CommonProgressBar
            actual={getNullToZero(rowData.actualConsumption)}
            previousPeriod={getNullToZero(rowData.baselineConsumption)}
            savings={rowData.change}
            savingsPercentage={getNullToZero(
              rowData.percentageConsumptionSaving
            )}
            progessBarRelativeWidth={progressBarRelativeWidth}
          />
        </span>
      </span>
    );
  }
  if (field === CarbonDetailMeterField.combustionType) {
    if (cellData.rowData.change < 0) {
      return (
        <span className="score-category-meterdata-meter ">
          <span>
            <Badge
              color={'red'}
              empty={true}
              className="breakdown-name-badge-css-override"
            />
          </span>
          <span className="score-category-Badge">{rowData.combustionType}</span>
        </span>
      );
    }
    return (
      <span className="score-category-meterdata-meter ">
        <span className="score-category-Badge">{rowData.combustionType}</span>
      </span>
    );
  }
  return <></>;
};

const getcellDataRenderer = (
  cellData: ICellData,
  utilityMeasurementUnit: string,
  progressBarRelativeWidth?: number,
  emissionMeasurementUnit?: string
) => {
  const { value, field, rowData } = cellData;

  if (field === CarbonDetailMeterField.actualConsumption) {
    const convertedValue = getConvertedValue(
      ConversionType.CARBON,
      emissionMeasurementUnit ? emissionMeasurementUnit : '',
      value,
      rowData.actualUnit
    )?.Value;

    return (
      <span className="emaas-carbondetail-category-meterdata">
        <span className="score-category-percent">
          <div className="measurementunit">
            {getNullConverted(convertedValue)}
          </div>
          {rowData.actualUnit}
        </span>
        <span className="score-category-progress">
          <CommonProgressBar
            actual={getNullToZero(rowData.actualConsumption)}
            previousPeriod={getNullToZero(rowData.baselineConsumption)}
            savings={rowData.change}
            savingsPercentage={getNullToZero(
              rowData.percentageConsumptionSaving
            )}
            progessBarRelativeWidth={progressBarRelativeWidth}
          />
        </span>
      </span>
    );
  }
  if (field === CarbonDetailMeterField.change) {
    console.log('rowData inn change', rowData);
    const convertedValue: IAny = getConvertedValue(
      ConversionType.CARBON,
      emissionMeasurementUnit ? emissionMeasurementUnit : '',
      value,
      rowData.changeUnit
    )?.Value;
    if (value === null) {
      return <>--</>;
    }
    return (
      <span className="score-category-meterdata-meter">
        <span
          className="savings-badge"
          style={{
            color: getPercentageColor(convertedValue)
          }}
        >
          {getConvertedValues(convertedValue)}
          {' ' +
            rowData.changeUnit +
            '(' +
            getConvertedValues(rowData.percentageConsumptionSaving) +
            '%' +
            ')'}
        </span>
      </span>
    );
  }
  if (field === CarbonDetailMeterField.baselineConsumption) {
    const convertedValue: IAny = getConvertedValue(
      ConversionType.CARBON,
      emissionMeasurementUnit ? emissionMeasurementUnit : '',
      value,
      rowData.baselineUnit
    )?.Value;
    return (
      <span className="score-category-meterdata-meter">
        <div className="consumptionvalue">
          {getNullConverted(convertedValue)}
        </div>
        {rowData.baselineUnit}
      </span>
    );
  }
  if (field === CarbonDetailMeterField.scope) {
    if (cellData.rowData.change < 0) {
      return (
        <span className="score-category-meterdata-meter ">
          <span>
            <Badge
              color={'red'}
              empty={true}
              className="breakdown-name-badge-css-override"
            />
          </span>
          <span className="score-category-Badge">{rowData.scope}</span>
        </span>
      );
    }
    return (
      <span className="score-category-meterdata-meter ">
        <span className="score-category-Badge">{rowData.scope}</span>
      </span>
    );
  }
  return <></>;
};

const DetailBreakdownByScope = (
  props: ICarbonDetailTabBreakdownByScopeProps
) => {
  const {
    meterData,
    utilityMeasurementUnit,
    columnDetails,
    // utilityType,
    isEnergy,
    subUtilityMeasurementUnit,
    subUtilitycolumnDetails
  } = props;
  const [fieldSortOrder, setFieldSortOrder] = useState<number>(-1);
  const [sortFieldName, setSortFieldName] = useState<string>(
    isEnergy
      ? EnergyDetailMeterField.actualConsumption
      : CarbonDetailMeterField.actualConsumption
  );

  const [expandedRows, setexpandedRows] = useState([
    meterData[getMaxValueIndexForField(meterData, 'totalActualEmission')]
  ]);

  useEffect(() => {
    setexpandedRows([]);
  }, []);

  const cellDataRenderer = (cellData: ICellData) => {
    return getcellDataRenderer(
      cellData,
      utilityMeasurementUnit,
      props.progressBarRelativeWidth,
      props.emissionMeasurementUnit
    );
  };

  const cellDataRendererForChildren = (cellData: ICellData) => {
    return getcellDataRendererForChildren(
      cellData,
      subUtilityMeasurementUnit,
      props.progressBarRelativeWidth,
      props.emissionMeasurementUnit
    );
  };

  const rowExpansionTemplate = (
    data: IAny,
    columnDetails: IDataTableColumn[],
    rowcellDataRendererForChildren: (cellData: ICellData) => JSX.Element
  ) => {
    return (
      <div className="UtilityBill-row-expansion">
        <DataTable data={data.emissionsBySubScope.values} showHeader={false}>
          <DataTable.Column initialWidth="1.5%" field="" header="" />
          {columnDetails.map((column: IDataTableColumn) => {
            return (
              <DataTable.Column
                key={column.field}
                className={column.className}
                field={column.field}
                header={column.header}
                sortable={column.sortable}
                renderer={rowcellDataRendererForChildren}
                initialWidth={column.initialWidth}
              />
            );
          })}
        </DataTable>
      </div>
    );
  };

  const toggleExpand = (
    data: IAny,
    open: boolean,
    expandedRows: IAny[],
    setexpandedRows: React.Dispatch<React.SetStateAction<IAny>>
  ) => {
    const expanded = [...expandedRows];
    if (open) {
      let index = expanded.findIndex(
        (item) => JSON.stringify(item) === JSON.stringify(data)
      );
      expanded.splice(index, 1);
    } else {
      expanded.push(data);
    }
    setexpandedRows(expanded);
  };

  const expanderTemplate = (
    data: ICellData,
    expandedRows: IAny[],
    setexpandedRows: React.Dispatch<React.SetStateAction<IAny>>
  ) => {
    const open =
      expandedRows.findIndex(
        (item) => JSON.stringify(item) === JSON.stringify(data.rowData)
      ) >= 0;
    return (
      <div
        onClick={() =>
          toggleExpand(data.rowData, open, expandedRows, setexpandedRows)
        }
      >
        <Icon
          root="common"
          size="small"
          name={open ? 'caret-down' : 'caret-right'}
        />
      </div>
    );
  };
  return (
    <div className="Savings-Datatable">
      <DataTable
        className="nonflat-table Savingtable"
        expandedRows={expandedRows}
        rowExpansionTemplate={(data: IAny) =>
          rowExpansionTemplate(
            data,
            subUtilitycolumnDetails,
            cellDataRendererForChildren
          )
        }
        data={meterData}
        onSort={({ sortField, sortOrder }) => {
          setFieldSortOrder(sortOrder as number);
          setSortFieldName(sortField);
        }}
        sortOrder={fieldSortOrder}
        sortField={sortFieldName}
      >
        <DataTable.Column
          initialWidth="1.5%"
          header=""
          field=""
          renderer={(data: ICellData) =>
            expanderTemplate(data, expandedRows, setexpandedRows)
          }
        />
        {renderTableColumns(columnDetails, cellDataRenderer)}
      </DataTable>
    </div>
  );
};
export default DetailBreakdownByScope;
